import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Badge, DataTable, Input, Modal } from '../../../components/ui';
import { Plus, User, Edit, Trash2, Search, Key, RefreshCw } from 'lucide-react';
import { UserRole } from '../../../types';
import { fetchAPI } from '../../../services/apiClient';

// ── Types ──────────────────────────────────────────────────────────────────

interface UsuarioItem {
  id: string;
  username: string;
  nombre: string;
  role: UserRole;
  banca_id: string | null;
  banca_nombre: string | null;
  local_id: string | null;
  activo: boolean;
  created_at: string | null;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const getRolBadge = (rol: UserRole) => {
  const config: Record<UserRole, { variant: 'danger' | 'primary' | 'warning' | 'success' | 'secondary'; label: string }> = {
    ADMIN: { variant: 'danger', label: 'Admin' },
    BANCA: { variant: 'primary', label: 'Banca' },
    LOCAL: { variant: 'warning', label: 'Local' },
    MINI_BANCA: { variant: 'secondary', label: 'Mini Banca' },
    VENDEDOR_CON_CAJA: { variant: 'success', label: 'Vendedor +Caja' },
    VENDEDOR_SIN_CAJA: { variant: 'secondary', label: 'Vendedor' },
  };
  return config[rol] || { variant: 'secondary' as const, label: rol };
};

const formatDate = (iso: string | null) => {
  if (!iso) return 'Nunca';
  return new Date(iso).toLocaleString('es-GT');
};

// ── Component ──────────────────────────────────────────────────────────────

export const UsuariosPage: React.FC = () => {
  const [usuarios, setUsuarios] = useState<UsuarioItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [rolFiltro, setRolFiltro] = useState<string>('');

  // Modal: cambiar contraseña
  const [modalPasswordOpen, setModalPasswordOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UsuarioItem | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const cargarUsuarios = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAPI<UsuarioItem[]>('/usuarios/admin/usuarios');
      setUsuarios(data);
    } catch (e: any) {
      setError(e.message || 'No se pudieron cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarUsuarios();
  }, []);

  const filteredUsuarios = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return usuarios.filter(u => {
      const matchSearch = !q ||
        u.nombre.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q) ||
        (u.banca_nombre || '').toLowerCase().includes(q);
      const matchRol = !rolFiltro || u.role === rolFiltro;
      return matchSearch && matchRol;
    });
  }, [usuarios, searchTerm, rolFiltro]);

  const conteosPorRol = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const u of usuarios) {
      counts[u.role] = (counts[u.role] || 0) + 1;
    }
    return counts;
  }, [usuarios]);

  const abrirModalPassword = (usuario: UsuarioItem) => {
    setSelectedUser(usuario);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
    setModalPasswordOpen(true);
  };

  const guardarPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    setPasswordError('');
    setPasswordSuccess('');

    if (!newPassword) {
      setPasswordError('Ingrese la nueva contraseña');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }

    setSavingPassword(true);
    try {
      await fetchAPI(`/usuarios/admin/usuarios/${selectedUser.id}/password`, {
        method: 'PATCH',
        body: JSON.stringify({ password: newPassword }),
      });
      setPasswordSuccess(`Contraseña de @${selectedUser.username} actualizada correctamente`);
      setNewPassword('');
      setConfirmPassword('');
      // Cerrar modal después de 1.5s
      setTimeout(() => {
        setModalPasswordOpen(false);
        setPasswordSuccess('');
      }, 1500);
    } catch (e: any) {
      setPasswordError(e.message || 'No se pudo cambiar la contraseña');
    } finally {
      setSavingPassword(false);
    }
  };

  const toggleEstado = async (usuario: UsuarioItem) => {
    const nuevoEstado = !usuario.activo;
    const accion = nuevoEstado ? 'activar' : 'desactivar';
    if (!window.confirm(`¿${accion.charAt(0).toUpperCase() + accion.slice(1)} a @${usuario.username}?`)) return;

    try {
      await fetchAPI(`/usuarios/admin/usuarios/${usuario.id}/estado`, {
        method: 'PATCH',
        body: JSON.stringify({ activo: nuevoEstado }),
      });
      await cargarUsuarios();
    } catch (e: any) {
      setError(e.message || `No se pudo ${accion} el usuario`);
    }
  };

  const roles: UserRole[] = ['ADMIN', 'BANCA', 'LOCAL', 'VENDEDOR_CON_CAJA', 'VENDEDOR_SIN_CAJA'];

  const columns = [
    {
      key: 'nombre',
      header: 'Usuario',
      render: (u: UsuarioItem) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
            <User className="text-gray-500" size={20} />
          </div>
          <div>
            <p className="font-medium">{u.nombre}</p>
            <p className="text-xs text-gray-500">@{u.username}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'rol',
      header: 'Rol',
      render: (u: UsuarioItem) => {
        const config = getRolBadge(u.role);
        return <Badge variant={config.variant}>{config.label}</Badge>;
      },
    },
    {
      key: 'banca',
      header: 'Banca',
      render: (u: UsuarioItem) => {
        if (u.role === 'ADMIN') return <span className="text-gray-400">-</span>;
        return <span>{u.banca_nombre || '-'}</span>;
      },
    },
    {
      key: 'estado',
      header: 'Estado',
      render: (u: UsuarioItem) => (
        <Badge variant={u.activo ? 'success' : 'secondary'}>
          {u.activo ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
    },
    {
      key: 'ultimoAcceso',
      header: 'Creado',
      render: (u: UsuarioItem) => (
        <span className="text-sm text-gray-500">{formatDate(u.created_at)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (u: UsuarioItem) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            title="Cambiar contraseña"
            onClick={() => abrirModalPassword(u)}
            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
          >
            <Key size={16} />
          </button>
          <button
            type="button"
            title={u.activo ? 'Desactivar usuario' : 'Activar usuario'}
            onClick={() => toggleEstado(u)}
            className={`p-2 rounded-lg ${
              u.activo
                ? 'text-gray-400 hover:text-warning-600 hover:bg-warning-50'
                : 'text-gray-400 hover:text-success-600 hover:bg-success-50'
            }`}
          >
            <Edit size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h1>
          <p className="text-gray-500">Administrar usuarios del sistema</p>
        </div>
        <Button
          variant="outline"
          leftIcon={<RefreshCw size={18} />}
          onClick={cargarUsuarios}
          isLoading={loading}
        >
          Actualizar
        </Button>
      </div>

      {/* Conteos por rol */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold">{usuarios.length}</p>
        </Card>
        {roles.slice(0, 4).map(rol => {
          const config = getRolBadge(rol);
          return (
            <Card key={rol}>
              <p className="text-sm text-gray-500">{config.label}</p>
              <p className="text-2xl font-bold">{conteosPorRol[rol] || 0}</p>
            </Card>
          );
        })}
      </div>

      {error && (
        <div className="bg-danger-50 text-danger-600 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Filtros */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Buscar por nombre, usuario o banca..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leftIcon={<Search size={18} />}
            />
          </div>
          <select
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            value={rolFiltro}
            onChange={(e) => setRolFiltro(e.target.value)}
          >
            <option value="">Todos los roles</option>
            {roles.map(rol => (
              <option key={rol} value={rol}>{getRolBadge(rol).label}</option>
            ))}
          </select>
        </div>
      </Card>

      {/* Tabla */}
      <Card padding="none">
        <DataTable
          columns={columns}
          data={filteredUsuarios}
          keyExtractor={(u) => u.id}
        />
      </Card>

      {/* Modal: cambiar contraseña */}
      <Modal
        isOpen={modalPasswordOpen}
        onClose={() => setModalPasswordOpen(false)}
        title={`Cambiar contraseña${selectedUser ? ` — @${selectedUser.username}` : ''}`}
      >
        <form onSubmit={guardarPassword} className="space-y-4">
          {selectedUser && (
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <p className="font-medium">{selectedUser.nombre}</p>
              <p className="text-gray-500">
                {getRolBadge(selectedUser.role).label}
                {selectedUser.banca_nombre ? ` — ${selectedUser.banca_nombre}` : ''}
              </p>
            </div>
          )}

          <Input
            label="Nueva contraseña"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Mínimo 6 caracteres"
            required
          />
          <Input
            label="Confirmar contraseña"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Repita la contraseña"
            required
          />

          {passwordError && (
            <div className="bg-danger-50 text-danger-600 px-4 py-3 rounded-lg text-sm">
              {passwordError}
            </div>
          )}
          {passwordSuccess && (
            <div className="bg-success-50 text-success-600 px-4 py-3 rounded-lg text-sm">
              {passwordSuccess}
            </div>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setModalPasswordOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              leftIcon={<Key size={18} />}
              isLoading={savingPassword}
            >
              Guardar contraseña
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default UsuariosPage;
